import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import {
  getProfiles,
  getProfileFilters,
  type ProfileQueryRequest,
  type ProfileQueryResponse,
  type ProfileFiltersResponse,
} from "@/services/api/companies";

const companiesKeys = {
  all: ["companies"] as const,
  profileFilters: () => [...companiesKeys.all, "profileFilters"] as const,
  profiles: (request: ProfileQueryRequest) => {
    const filterKey = Object.entries(request)
      .filter(
        ([key, value]) =>
          value !== undefined &&
          value !== null &&
          value !== "" &&
          key !== "page" &&
          key !== "pageSize"
      )
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => `${key}:${String(value)}`)
      .join(",");
    return [
      ...companiesKeys.all,
      "profiles",
      request.page ?? 1,
      request.pageSize ?? 30,
      filterKey || "no-filters",
    ] as const;
  },
};

export function useCompanyProfiles(
  request: ProfileQueryRequest,
  options?: Omit<
    UseQueryOptions<ProfileQueryResponse, Error>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery<ProfileQueryResponse, Error>({
    queryKey: companiesKeys.profiles(request),
    queryFn: () => getProfiles(request),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useProfileFilters(
  options?: Omit<
    UseQueryOptions<ProfileFiltersResponse, Error>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery<ProfileFiltersResponse, Error>({
    queryKey: companiesKeys.profileFilters(),
    queryFn: getProfileFilters,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}
